import { useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { ENV_NAME_RE, VAR_NAME_RE, type SharedValueDTO } from '@devrobot/shared';
import { Eye, EyeOff, KeyRound, Lock, Pencil, Plus, Search, ShieldAlert, Trash2 } from 'lucide-react';
import { api, seg } from '../api/client';
import { qk, useApiMutation, useSharedValues } from '../api/queries';
import { useUser } from '../api/user';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CopyButton } from '../components/CopyButton';
import { EmptyState } from '../components/EmptyState';
import { ErrorAlert } from '../components/ErrorAlert';
import { Modal } from '../components/Modal';
import { PageHeader } from '../components/PageHeader';
import { RelativeTime } from '../components/RelativeTime';
import { SkeletonTable } from '../components/Skeleton';
import { TagsInput } from '../components/TagsInput';
import { useToast } from '../components/Toaster';
import { Haptic } from '../kit/haptics';
import { listTransition } from '../kit/hooks';
import { haptic } from '../kit/native';

const VAR_RE = VAR_NAME_RE;
const ENV_RE = ENV_NAME_RE;

interface EditorState {
  existing: SharedValueDTO | null;
}

function SharedEditor({ existing, onClose }: { existing: SharedValueDTO | null; onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [secret, setSecret] = useState(existing?.secret ?? true);
  const [envs, setEnvs] = useState<string[]>(existing?.allowedEnvironments ?? ['development', 'staging']);
  const [allowProtected, setAllowProtected] = useState(existing?.allowProtected ?? false);
  const [replaceValue, setReplaceValue] = useState(!existing);
  const [value, setValue] = useState('');
  const save = useApiMutation(
    (body: Record<string, unknown>) => (existing ? api.patch<SharedValueDTO>(`/api/v1/shared-values/${seg(existing.id)}`, body) : api.post<SharedValueDTO>('/api/v1/shared-values', body)),
    { invalidate: [qk.shared, ['project']] },
  );
  const nameValid = VAR_RE.test(name);
  const consumers = existing?.consumers ?? [];

  return (
    <Modal title={existing ? `Edit ${existing.name}` : 'New shared value'} onClose={onClose} size="lg">
      <form
        className="space-y-3"
        onInvalidCapture={() => haptic('error')}
        onSubmit={(e) => {
          e.preventDefault();
          const body: Record<string, unknown> = { description, allowedEnvironments: envs, allowProtected };
          if (!existing) Object.assign(body, { name, secret });
          if (replaceValue) body.value = value;
          save.mutate(body, {
            onSuccess: () => {
              if (existing) toast.success(`${existing.name} updated`, replaceValue && consumers.length ? { description: 'Bound targets now show pending configuration until it is applied.' } : undefined);
              else toast.success(`${name} created`, { description: 'Bind it from a project’s configuration.' });
              onClose();
            },
            onError: () => haptic('error'),
          });
        }}
      >
        {!existing ? (
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Name</legend>
            <input className={`input w-full font-mono ${name && !nameValid ? 'input-error' : ''}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="SMTP_URL" required />
            <p className="label">Environment-variable syntax. Applications bind to it explicitly; it is never injected automatically.</p>
          </fieldset>
        ) : null}
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Description</legend>
          <input className="input w-full" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What it is and who owns it" />
        </fieldset>
        {!existing ? (
          <label className="flex cursor-pointer items-start gap-3 rounded-field bg-base-200/60 p-3">
            <input type="checkbox" className="toggle toggle-primary mt-0.5" checked={secret} onChange={(e) => setSecret(e.target.checked)} />
            <span>
              <span className="font-medium">Secret</span>
              <span className="block text-sm text-base-content/60">Secret values are masked everywhere and can only be revealed by an administrator (audited).</span>
            </span>
          </label>
        ) : null}
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Allowed environments</legend>
          <TagsInput
            label="Allowed environments"
            value={envs}
            onChange={setEnvs}
            suggestions={['development', 'staging', 'production', '*']}
            placeholder="Type an environment name and press Enter"
            validate={(t) => (t === '*' || ENV_RE.test(t) ? null : `"${t}" is not a valid environment name`)}
          />
          <p className="label whitespace-normal">Bindings from other environments are rejected. “*” allows every environment name.</p>
        </fieldset>
        <label className="flex cursor-pointer items-start gap-3 rounded-field bg-base-200/60 p-3">
          <input type="checkbox" className="toggle toggle-warning mt-0.5" checked={allowProtected} onChange={(e) => setAllowProtected(e.target.checked)} />
          <span>
            <span className="font-medium">Allow protected environments</span>
            <span className="block text-sm text-base-content/60">Required before a protected environment (such as production) can bind this value, even if it is listed above.</span>
          </span>
        </label>
        {existing ? (
          <label className="flex cursor-pointer items-center gap-3">
            <input type="checkbox" className="checkbox checkbox-sm" checked={replaceValue} onChange={(e) => setReplaceValue(e.target.checked)} />
            <span className="font-medium">Replace the value</span>
          </label>
        ) : null}
        {replaceValue ? (
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Value</legend>
            <textarea className="textarea w-full font-mono" rows={3} value={value} onChange={(e) => setValue(e.target.value)} autoComplete="off" spellCheck={false} placeholder={existing?.secret ?? secret ? 'Secret value (stored encrypted)' : 'Value'} />
            <p className="label">An empty value is stored as intentionally empty.</p>
          </fieldset>
        ) : null}
        {existing && replaceValue && consumers.length > 0 ? (
          <div role="note" className="alert alert-warning alert-soft text-sm">
            <div>
              <p className="font-medium">Used by {consumers.length} binding{consumers.length === 1 ? '' : 's'}</p>
              <p>Saving does not restart anything. Affected targets show pending configuration until someone applies it.</p>
            </div>
          </div>
        ) : null}
        <ErrorAlert error={save.error} title="The shared value was not saved" />
        <div className="modal-action">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <Haptic disabled={save.isPending || (!existing && !nameValid) || envs.length === 0}>
            <button type="submit" className="btn btn-primary" disabled={save.isPending || (!existing && !nameValid) || envs.length === 0}>
              {save.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : null}
              {existing ? 'Save changes' : 'Create shared value'}
            </button>
          </Haptic>
        </div>
      </form>
    </Modal>
  );
}

function Consumers({ v }: { v: SharedValueDTO }) {
  if (v.consumers.length === 0) return <span className="text-sm text-base-content/50">unused</span>;
  return (
    <details className="group">
      <summary className="cursor-pointer text-sm text-base-content/80 marker:text-base-content/40">
        {v.consumers.length} binding{v.consumers.length === 1 ? '' : 's'}
      </summary>
      <ul className="mt-1 space-y-0.5 text-xs">
        {v.consumers.map((c, i) => (
          <li key={i}>
            <Link className="link link-hover link-primary" to={`/projects/${c.project}?tab=config&env=${c.environment}`}>
              {c.project}/{c.environment}
            </Link>{' '}
            as <span className="font-mono">{c.variable}</span>
            {c.targets.length ? <span className="text-base-content/60"> · {c.targets.length} target(s)</span> : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

type Filter = 'all' | 'secret' | 'plain' | 'unused';
const FILTERS: [Filter, string][] = [
  ['all', 'All'],
  ['secret', 'Secret'],
  ['plain', 'Plain'],
  ['unused', 'Unused'],
];

const vtName = (prefix: string, id: string) => ({ '--vt': `${prefix}-${id.replace(/[^a-zA-Z0-9_-]/g, '-')}` }) as CSSProperties;

export default function SharedValuesPage() {
  const user = useUser();
  const toast = useToast();
  const q = useSharedValues();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [deleting, setDeleting] = useState<SharedValueDTO | null>(null);
  const [revealing, setRevealing] = useState<SharedValueDTO | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const del = useApiMutation((id: string) => api.delete<{ ok: true }>(`/api/v1/shared-values/${seg(id)}`), { invalidate: [qk.shared] });
  const reveal = useApiMutation((id: string) => api.post<{ value: string }>(`/api/v1/shared-values/${seg(id)}/reveal`));

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (q.data ?? []).filter((v) => {
      if (filter === 'secret' && !v.secret) return false;
      if (filter === 'plain' && v.secret) return false;
      if (filter === 'unused' && v.consumers.length > 0) return false;
      if (!needle) return true;
      return [v.name, v.description ?? '', ...v.allowedEnvironments, ...v.consumers.map((c) => `${c.project}/${c.environment} ${c.variable}`)].some((s) => s.toLowerCase().includes(needle));
    });
  }, [q.data, search, filter]);

  const hide = (id: string) => setRevealed(({ [id]: _, ...rest }) => rest);

  const valueCell = (v: SharedValueDTO) =>
    revealed[v.id] !== undefined ? (
      <span className="flex flex-wrap items-center gap-1">
        <code className="break-anywhere rounded-[0.35rem] bg-warning/15 px-1.5 py-0.5 font-mono text-xs">{revealed[v.id] || '(empty)'}</code>
        <CopyButton value={revealed[v.id]} label={`Copy ${v.name}`} />
        <button type="button" className="btn btn-ghost btn-xs" onClick={() => hide(v.id)}>
          <EyeOff className="size-3.5" aria-hidden="true" />
          Hide
        </button>
      </span>
    ) : (
      <span className="flex flex-wrap items-center gap-1">
        <code className="break-anywhere font-mono text-xs text-base-content/65">{v.displayValue ?? (v.secret ? '••••••••' : '—')}</code>
        {v.secret && user.platformAdmin ? (
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => setRevealing(v)} aria-label={`Reveal ${v.name}`}>
            <Eye className="size-3.5" aria-hidden="true" />
            Reveal
          </button>
        ) : null}
      </span>
    );

  const envBadges = (v: SharedValueDTO) => (
    <div className="flex flex-wrap gap-1">
      {v.allowedEnvironments.map((e) => (
        <span key={e} className="badge badge-ghost badge-sm font-mono">
          {e}
        </span>
      ))}
    </div>
  );

  const protectedBadge = (v: SharedValueDTO) =>
    v.allowProtected ? (
      <span className="badge badge-warning badge-soft badge-sm gap-1">
        <ShieldAlert className="size-3" aria-hidden="true" />
        allowed
      </span>
    ) : (
      <span className="text-sm text-base-content/55">not allowed</span>
    );

  const actions = (v: SharedValueDTO) =>
    user.platformAdmin ? (
      <span className="inline-flex items-center gap-0.5">
        <button type="button" className="btn btn-square btn-ghost btn-xs" onClick={() => setEditor({ existing: v })} aria-label={`Edit ${v.name}`}>
          <Pencil className="size-3.5" aria-hidden="true" />
        </button>
        <span className={v.consumers.length ? 'tooltip tooltip-left' : ''} data-tip={v.consumers.length ? 'Remove the bindings first' : undefined}>
          <button type="button" className="btn btn-square btn-ghost btn-xs dr-ink-error" disabled={v.consumers.length > 0} onClick={() => setDeleting(v)} aria-label={`Delete ${v.name}`}>
            <Trash2 className="size-3.5" aria-hidden="true" />
          </button>
        </span>
      </span>
    ) : null;

  const nameBlock = (v: SharedValueDTO) => (
    <>
      <div className="flex items-center gap-1.5 font-mono font-medium break-anywhere">
        {v.secret ? <Lock className="size-3.5 shrink-0 text-base-content/55" aria-label="secret" /> : null}
        {v.name}
      </div>
      {v.description ? <div className="text-xs text-base-content/60">{v.description}</div> : null}
    </>
  );

  const version = (v: SharedValueDTO) => (
    <>
      v{v.version}
      <span className="text-base-content/55">
        {' · '}
        <RelativeTime value={v.updatedAt} />
        {v.updatedBy ? ` by ${v.updatedBy}` : ''}
      </span>
    </>
  );

  return (
    <>
      <PageHeader
        title="Shared values"
        subtitle="Values reused by several applications through an explicit binding in each application's environment configuration. A shared value never reaches a container unless an application binds it."
        actions={
          user.platformAdmin ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditor({ existing: null })}>
              <Plus className="size-4" aria-hidden="true" />
              New shared value
            </button>
          ) : null
        }
      />
      {q.isPending ? <SkeletonTable /> : null}
      {q.isError ? <ErrorAlert error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data && q.data.length === 0 ? (
        <EmptyState icon={KeyRound} title="No shared values yet">
          {user.platformAdmin ? 'Create one for credentials used by several applications, such as an SMTP relay URL. Projects then bind it from their Configuration tab.' : 'A platform administrator can create shared values for credentials used by several applications.'}
        </EmptyState>
      ) : null}
      {q.data && q.data.length > 0 ? (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="input w-full lg:w-72">
              <Search className="size-4 opacity-50" aria-hidden="true" />
              <input type="search" data-page-search placeholder="Search shared values" aria-label="Search shared values" value={search}
                onChange={(e) => {
                  // Read now, not inside the transition: the input is controlled, so React restores
                  // the previous value on the DOM node before the deferred callback runs.
                  const next = e.target.value;
                  listTransition(() => setSearch(next));
                }}
              />
              <kbd className="kbd kbd-sm max-sm:hidden">/</kbd>
            </label>
            <div role="radiogroup" aria-label="Filter shared values" className="tabs tabs-box tabs-sm w-fit max-w-full flex-nowrap overflow-x-auto border border-hairline">
              {FILTERS.map(([id, label]) => (
                <label key={id} className="tab shrink-0 whitespace-nowrap has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-primary">
                  <input type="radio" name="shared-filter" className="sr-only" checked={filter === id} onChange={() => listTransition(() => setFilter(id))} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {shown.length === 0 ? (
            <EmptyState icon={Search} title="No shared values match">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  listTransition(() => {
                    setSearch('');
                    setFilter('all');
                  })
                }
              >
                Clear search and filters
              </button>
            </EmptyState>
          ) : (
            <div className="card dr-surface overflow-hidden">
              <ul className="divide-y divide-hairline md:hidden">
                {shown.map((v) => (
                  <li key={v.id} data-vt style={vtName('sv-card', v.id)} className="space-y-2 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">{nameBlock(v)}</div>
                      {actions(v)}
                    </div>
                    {valueCell(v)}
                    {envBadges(v)}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-base-content/60">
                      <span className="inline-flex items-center gap-1">Protected: {protectedBadge(v)}</span>
                      <span>{version(v)}</span>
                    </div>
                    <Consumers v={v} />
                  </li>
                ))}
              </ul>
              <div className="overflow-x-auto max-md:hidden">
                <table className="table table-sm">
                  <thead>
                    <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                      <th>Name</th>
                      <th>Value</th>
                      <th>Allowed environments</th>
                      <th>Protected</th>
                      <th className="max-lg:hidden">Version</th>
                      <th>Consumers</th>
                      <th>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((v) => (
                      <tr key={v.id} data-vt style={vtName('sv-row', v.id)} className="border-hairline align-top hover:bg-base-200/50">
                        <td className="min-w-48">{nameBlock(v)}</td>
                        <td className="max-w-xs">{valueCell(v)}</td>
                        <td>{envBadges(v)}</td>
                        <td>{protectedBadge(v)}</td>
                        <td className="text-sm whitespace-nowrap max-lg:hidden">
                          v{v.version}
                          <div className="text-xs text-base-content/55">
                            <RelativeTime value={v.updatedAt} />
                            {v.updatedBy ? ` by ${v.updatedBy}` : ''}
                          </div>
                        </td>
                        <td>
                          <Consumers v={v} />
                        </td>
                        <td className="text-right whitespace-nowrap">{actions(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {editor ? <SharedEditor existing={editor.existing} onClose={() => setEditor(null)} /> : null}
      {deleting ? (
        <ConfirmDialog
          title={`Delete ${deleting.name}?`}
          confirmLabel="Delete shared value"
          tone="error"
          typedConfirmation={deleting.name}
          busy={del.isPending}
          error={del.error}
          onCancel={() => (setDeleting(null), del.reset())}
          onConfirm={() => del.mutate(deleting.id, { onSuccess: () => (toast.success(`${deleting.name} deleted`), setDeleting(null)), onError: () => haptic('error') })}
        >
          <p>The value and its version history are deleted permanently. No application binds it, so no running application is affected.</p>
        </ConfirmDialog>
      ) : null}
      {revealing ? (
        <ConfirmDialog
          title={`Reveal ${revealing.name}?`}
          confirmLabel="Reveal value"
          tone="warning"
          busy={reveal.isPending}
          error={reveal.error}
          onCancel={() => (setRevealing(null), reveal.reset())}
          onConfirm={() =>
            reveal.mutate(revealing.id, {
              onSuccess: (r) => {
                setRevealed((m) => ({ ...m, [revealing.id]: r.value }));
                setRevealing(null);
              },
              onError: () => haptic('error'),
            })
          }
        >
          <p>The plaintext value is shown on this page. The reveal is recorded in the audit log with your name.</p>
        </ConfirmDialog>
      ) : null}
    </>
  );
}
